import {gql} from '@apollo/client';
import {Badge, Box, Group, Paper, Stack, Text} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import * as NavConstants from 'constants/NavConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {RootCrumb} from 'nav/Breadcrumbs';
import ListingHeader from 'nav/sidepanel/header/ListingHeader';
import ToggleDropdown from 'nav/ToggleDropdown';
import notifications from 'notifications';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as nyc from 'ui-library/NYC';

import {MCPSidepanelQuery} from './__generated/MCPSidepanelQuery';
import AddMCPServerModal from './AddMCPServerModal';

export const MCP_SIDEPANEL_QUERY = gql`
  query MCPSidepanelQuery @skipAudit @engComponent(name: LLM) {
    mcpServerConfigs {
      id
      name
      description
      isEnabled
      createdAt
      modifiedAt
    }
  }
`;

interface MCPSidepanelProps {
  isExpanded: boolean;
  isCompactDisplay: boolean;
  setIsCompactDisplay: (isCompact: boolean) => void;
  activeItemId: string | null;
  connectionsTab: JSX.Element;
}

type MCPServerConfig = MCPSidepanelQuery['mcpServerConfigs'][number];

function MCPConnectionCard(props: {config: MCPServerConfig; isCompact: boolean}): JSX.Element {
  const {config, isCompact} = props;
  const statusColor = config.isEnabled ? 'green' : 'gray';
  const statusText = config.isEnabled ? 'enabled' : 'disabled';

  return (
    <Paper className={css(styles.card)} p={16} radius={4} withBorder={true} shadow="xs">
      <Group noWrap={true} spacing="sm" align="flex-start">
        <Box className={css(styles.mcpLogo)} />
        <Stack spacing={4} style={{flex: 1, minWidth: 0}}>
          <Group position="apart" noWrap={true}>
            <Text className={css(styles.cardTitle)} lineClamp={1}>
              {config.name}
            </Text>
            <Badge size="md" color={statusColor} variant="filled" radius="sm">
              {statusText}
            </Badge>
          </Group>
          {!isCompact && config.modifiedAt && (
            <Text className={css(styles.cardMetaText)}>
              Modified: {new Date(config.modifiedAt).toLocaleDateString()}
            </Text>
          )}
        </Stack>
      </Group>
    </Paper>
  );
}

function MCPSidepanelHeader(props: MCPSidepanelProps & {onAddClick: () => void}): JSX.Element {
  return (
    <ListingHeader
      buttons={
        <>
          <ToggleDropdown className="u-mini-spacing-right" expanded={props.isExpanded} />
          <Button
            buttonStyle={ButtonStyle.SECONDARY}
            onClick={props.onAddClick}
            size={ButtonSize.SMALL}
            icon="plus"
            // text="Add MCP Server"
            className="u-mini-spacing-right"
          />
        </>
      }
      crumbs={<RootCrumb text="MCPs" />}
      isExpanded={props.isExpanded}
      queryInput={<div />}
      filtersContainer={<div />}
      sectionTabs={props.connectionsTab}
    />
  );
}

function CollapsedMCPSidepanelContents(
  props: MCPSidepanelProps & {configs: Array<MCPServerConfig>; isLoading: boolean; onAddClick: () => void}
): JSX.Element {
  return (
    <>
      <nyc.Row>
        <MCPSidepanelHeader {...props} />
      </nyc.Row>
      <nyc.Row
        aria-labelledby={`${NavConstants.Section.MCPS}_TAB`}
        className="u-vertical-flex"
        id={`${NavConstants.Section.MCPS}_PANEL`}
        fluid={true}
        role="tabpanel"
      >
        <div className="u-flex-grow u-position-relative">
          <div className="u-absolute-scroll-container">
            {props.isLoading ? (
              <CenteredHorizontalThrobber />
            ) : (
              <div className={css(styles.cardContainer)}>
                {props.configs.length === 0 ? (
                  <div className="sidepanel-fadedMessage">No MCP server configurations</div>
                ) : (
                  props.configs.map((config) => (
                    <MCPConnectionCard key={config.id} config={config} isCompact={props.isCompactDisplay} />
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </nyc.Row>
    </>
  );
}

function ExpandedMCPSidepanelContents(
  props: MCPSidepanelProps & {configs: Array<MCPServerConfig>; isLoading: boolean; onAddClick: () => void}
): JSX.Element {
  return (
    <nyc.Row fluid={true}>
      <nyc.Col lg={1} />
      <nyc.Col lg={20} className="u-flex-display u-vertical-flex">
        <MCPSidepanelHeader {...props} />
        <div
          aria-labelledby={NavConstants.Section.MCPS}
          className="u-flex-grow u-flex-display u-small-spacing-top"
          role="tabpanel"
        >
          {props.isLoading ? (
            <CenteredHorizontalThrobber />
          ) : (
            <div className={css(styles.expandedContainer)}>
              {props.configs.length === 0 ? (
                <div className="sidepanel-fadedMessage">No MCP server configurations</div>
              ) : (
                props.configs.map((config) => (
                  <MCPConnectionCard key={config.id} config={config} isCompact={props.isCompactDisplay} />
                ))
              )}
            </div>
          )}
        </div>
      </nyc.Col>
      <nyc.Col lg={1} />
    </nyc.Row>
  );
}

export default function MCPSidepanel(props: MCPSidepanelProps): JSX.Element {
  const [showAddModal, setShowAddModal] = useState(false);
  const queryResult = useBenchlingQuery<MCPSidepanelQuery>(MCP_SIDEPANEL_QUERY);

  const isLoading = queryResult.type === QueryResultType.PENDING;
  const isErrored = queryResult.type === QueryResultType.ERROR;
  let configs: Array<MCPServerConfig> = [];

  if (isErrored) {
    notifications.error('Could not load MCP server configurations; try reloading the page.');
  } else if (!isLoading && queryResult.type === QueryResultType.SUCCESS) {
    configs = queryResult.data.mcpServerConfigs;
  }

  const handleAddClick = (): void => {
    setShowAddModal(true);
  };

  const handleModalClose = (): void => {
    setShowAddModal(false);
  };

  const handleModalSubmit = (): void => {
    setShowAddModal(false);
    queryResult.refetch();
  };

  return (
    <>
      {showAddModal && (
        <AddMCPServerModal onRequestClose={handleModalClose} onRequestSubmit={handleModalSubmit} />
      )}
      <nyc.Container
        className={classNames('sidepanel', {expandedContainer: props.isExpanded})}
        size={props.isExpanded ? 'lg' : 'sidepanel'}
      >
        {props.isExpanded ? (
          <ExpandedMCPSidepanelContents
            {...props}
            configs={configs}
            isLoading={isLoading}
            onAddClick={handleAddClick}
          />
        ) : (
          <CollapsedMCPSidepanelContents
            {...props}
            configs={configs}
            isLoading={isLoading}
            onAddClick={handleAddClick}
          />
        )}
      </nyc.Container>
    </>
  );
}

const styles = StyleSheet.create({
  card: {
    cursor: 'pointer',
    height: '100%',
    overflow: 'hidden',
    transition: 'box-shadow 0.15s ease-in-out, transform 0.15s ease-in-out',
    ':hover': {
      boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.1)',
      transform: 'translateY(-2px)',
    },
  },
  cardTitle: {
    color: 'var(--gray-900, #1B1B1F)',
    fontSize: 13,
    fontWeight: 600,
    lineHeight: '16px',
  },
  cardMetaText: {
    color: 'var(--gray-700, #49494D)',
    fontSize: 12,
    fontWeight: 400,
    lineHeight: '14px',
  },
  mcpLogo: {
    width: 16,
    height: 16,
    borderRadius: 4,
    backgroundColor: 'var(--gray-100, #F4F4F5)',
    border: '1px solid var(--gray-200, #E2E3E5)',
    flexShrink: 0,
  },
  cardContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gridAutoRows: 60,
    gap: 12,
    padding: 12,
  },
  expandedContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gridAutoRows: 60,
    gap: 12,
    width: '100%',
    padding: 12,
  },
});
